/**
 * New announcement / a draft (1.7.6, D-1.7.6-1…3): the subject in both languages, the boxes the first draft is
 * written from, the message box (worker D's DraftBox — a draft is asked for once there is a title), who it goes to
 * (five choices) plus "Also send to", the count by group before the press — naming the tenants who have no email —
 * the fixed line on how hiding works, the Preview (the server's own HTML in a sandboxed frame), Save as a draft, and
 * Send in two presses. Nothing leaves without the second press; then the announcement's page opens.
 */
import { useEffect, useMemo, useRef, useState, type RefObject } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ANNOUNCE_MAX_SUBJECT,
  ANNOUNCE_PROGRAMS,
  AUDIENCE_KINDS,
  parseAddresses,
  type AnnounceLang,
  type AnnounceProgram,
  type AnnouncementDetail,
  type AnnouncementInput,
  type AnnouncementPreview,
  type AnnouncementView,
  type AudienceInput,
  type AudienceKind,
  type AudiencePreview,
  type DraftFields,
  type DraftResult,
} from '@shared/announcements';
import { useAnnounce } from '@/components/Announcer';
import { Icon } from '@/components/Icons';
import { Button } from '@/components/ui/button';
import { SheetContent } from '@/components/ui/sheet';
import { useToast } from '@/components/ui/use-toast';
import { useEvents } from '@/features/events/api';
import { errorText } from '@/features/people/api';
import { previewAnnouncement, requestAudience, saveAnnouncement, sendAnnouncement, useAnnouncementRefresh, useAudiencePeople, useExtras } from './api';
import { DraftBox } from './DraftBox';
import { groupsLine, nyWhen } from './words';

interface Picked {
  kind: 'tenant' | 'volunteer';
  id: string;
  name: string;
  unit: string;
  hasEmail: boolean;
}

const NAMES_SHOWN = 12;
const toIso = (k: string | undefined) => (k && /^\d{8}$/.test(k) ? `${k.slice(0, 4)}-${k.slice(4, 6)}-${k.slice(6, 8)}` : '');
const toClock = (m: number | null | undefined) => (typeof m === 'number' ? `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}` : '');
const toMinutes = (clock: string) => (/^\d{2}:\d{2}$/.test(clock) ? Number(clock.slice(0, 2)) * 60 + Number(clock.slice(3, 5)) : null);
const person = (p: { name: string; unit: string }) => (p.unit ? `${p.name} (${p.unit})` : p.name);

export function NewAnnouncementSheet({
  existing,
  onClose,
  onSent,
  returnFocusTo,
}: {
  /** A saved draft, opened to be changed or sent. Absent: a new announcement. */
  existing?: AnnouncementView | null;
  onClose: () => void;
  /** After the second press of Send: the announcement as it was queued. */
  onSent: (d: AnnouncementDetail) => void;
  returnFocusTo: RefObject<HTMLElement | null>;
}) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language === 'es' ? 'es' : 'en';
  const announce = useAnnounce();
  const toast = useToast();
  const refresh = useAnnouncementRefresh();
  const events = useEvents();

  const f = existing?.fields ?? null;
  const [id, setId] = useState<string | null>(existing?.id ?? null);
  const [subjectEn, setSubjectEn] = useState(existing?.subject.en ?? '');
  const [subjectEs, setSubjectEs] = useState(existing?.subject.es ?? '');
  const [eventId, setEventId] = useState<string | null>(existing?.eventId ?? null);
  const [title, setTitle] = useState(f?.title ?? '');
  const [day, setDay] = useState(toIso(f?.dayKey));
  const [time, setTime] = useState(toClock(f?.minutes));
  const [place, setPlace] = useState(f?.place ?? '');
  const [bring, setBring] = useState(f?.bring ?? '');
  const [accessibility, setAccessibility] = useState(f?.accessibility ?? '');
  const [raffle, setRaffle] = useState(f?.raffle ?? false);
  const [raffleRules, setRaffleRules] = useState(f?.raffleRules ?? '');
  const [body, setBody] = useState<{ en: string; es: string }>(existing?.body ?? { en: '', es: '' });
  const [kind, setKind] = useState<AudienceKind>(existing?.audience.kind ?? 'tenants');
  const [program, setProgram] = useState<AnnounceProgram>(existing?.audience.program ?? 'housekeeping');
  // a draft keeps the ids it was saved with; the names come back when the person is found again
  const [picked, setPicked] = useState<Picked[]>(() => [
    ...(existing?.audience.tenantIds ?? []).map((x): Picked => ({ kind: 'tenant', id: x, name: x, unit: '', hasEmail: true })),
    ...(existing?.audience.volunteerIds ?? []).map((x): Picked => ({ kind: 'volunteer', id: x, name: x, unit: '', hasEmail: true })),
  ]);
  const [q, setQ] = useState('');
  const [also, setAlso] = useState(existing?.audience.extras ?? '');
  const [listOpen, setListOpen] = useState(false);
  const [listTicked, setListTicked] = useState<string[]>([]);
  const [count, setCount] = useState<{ key: string; data: AudiencePreview } | null>(null);
  const [counting, setCounting] = useState(false);
  const [countError, setCountError] = useState<string | null>(null);
  const [preview, setPreview] = useState<{ key: string; data: AnnouncementPreview } | null>(null);
  const [previewLang, setPreviewLang] = useState<AnnounceLang>('en');
  const [busy, setBusy] = useState<'save' | 'preview' | 'send' | null>(null);
  const [sure, setSure] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);

  const people = useAudiencePeople(kind === 'people' ? q : '');
  const extras = useExtras(listOpen);

  const audience = useMemo<AudienceInput>(
    () => ({
      kind,
      ...(kind === 'program' ? { program } : {}),
      ...(kind === 'people' ? { tenantIds: picked.filter((p) => p.kind === 'tenant').map((p) => p.id), volunteerIds: picked.filter((p) => p.kind === 'volunteer').map((p) => p.id) } : {}),
      extras: also,
    }),
    [kind, program, picked, also],
  );
  const fields = useMemo<Partial<DraftFields>>(() => {
    const tenants = picked.some((p) => p.kind === 'tenant');
    const volunteers = picked.some((p) => p.kind === 'volunteer');
    const who: DraftFields['audience'] = kind === 'volunteers' ? 'volunteers' : kind === 'both' ? 'both' : kind === 'people' && volunteers ? (tenants ? 'both' : 'volunteers') : 'tenants';
    return {
      ...(title.trim() ? { title: title.trim() } : {}),
      dayKey: day.replace(/-/g, ''),
      minutes: toMinutes(time),
      place: place.trim(),
      bring: bring.trim(),
      accessibility: accessibility.trim(),
      rsvp: !!eventId,
      audience: who,
      raffle,
      raffleRules: raffle ? raffleRules.trim() : '',
    };
  }, [title, day, time, place, bring, accessibility, eventId, kind, picked, raffle, raffleRules]);
  /** What the draft is written from; nothing until there is a title. */
  const request = useMemo(() => (title.trim() ? { eventId, fields } : null), [title, eventId, fields]);
  const input = useMemo<AnnouncementInput>(() => ({ id, subject: { en: subjectEn.trim(), es: subjectEs.trim() }, body, audience, eventId, fields }), [id, subjectEn, subjectEs, body, audience, eventId, fields]);
  const inputKey = JSON.stringify(input);
  // D-1028-ONELANG: the Spanish version exists only with both its subject and its words (the server says the same)
  const spanish = !!body.es.trim() && !!subjectEs.trim();
  const audienceKey = JSON.stringify([audience, spanish]);

  // the count before the press: asked again a moment after the audience or the extra box changes
  const asked = useRef(0);
  useEffect(() => {
    const mine = ++asked.current;
    setCounting(true);
    const timer = setTimeout(() => {
      requestAudience(audience, spanish)
        .then((data) => {
          if (asked.current !== mine) return;
          setCount({ key: audienceKey, data });
          setCountError(null);
        })
        .catch((e: unknown) => {
          if (asked.current === mine) setCountError(errorText(e));
        })
        .finally(() => {
          if (asked.current === mine) setCounting(false);
        });
    }, 400);
    return () => clearTimeout(timer);
    // audienceKey is the audience and the language, as text
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [audienceKey]);

  const c = count?.data ?? null;
  const fresh = count?.key === audienceKey;
  const typed = useMemo(() => parseAddresses(also), [also]);
  // what is typed is checked here at once; the server's own list counts only for the text it was asked about
  const bad = useMemo(() => [...new Set([...typed.bad, ...(fresh && c ? c.badAddresses : [])])], [typed, fresh, c]);
  const shown = preview && preview.key === inputKey ? preview.data : null;
  const blocked = shown?.blocked ?? null;

  const say = (m: string, isBad = false) => {
    if (isBad) {
      setProblem(m);
      setNote(null);
    } else {
      setNote(m);
      setProblem(null);
    }
    announce(m, isBad ? 'assertive' : 'polite');
  };
  /** What is missing, in words, and the box to go to. */
  const missing = (): boolean => {
    if (!subjectEn.trim() && !subjectEs.trim()) {
      say(t('ann176.sheet.needSubject'), true);
      document.getElementById('ann-subject-en')?.focus();
      return true;
    }
    if (!body.en.trim()) {
      say(t('ann176.sheet.needBody'), true);
      return true;
    }
    return false;
  };

  const pickEvent = (value: string) => {
    const e = events.data?.upcoming.find((x) => x.eventId === value) ?? null;
    setEventId(e ? e.eventId : null);
    if (!e) return;
    setTitle(e.name);
    setDay(toIso(e.dayKey));
    setTime(toClock(e.minutes));
    setPlace(e.place);
    setRaffle(e.rule === 'raffle');
  };
  const onDrafted = (r: DraftResult) => {
    setSubjectEn((s) => (s.trim() ? s : r.en.subject));
    setSubjectEs((s) => (s.trim() ? s : r.es.subject));
  };
  const tick = (p: Picked, on: boolean) => setPicked((list) => (on ? (list.some((x) => x.kind === p.kind && x.id === p.id) ? list.map((x) => (x.kind === p.kind && x.id === p.id ? p : x)) : [...list, p]) : list.filter((x) => !(x.kind === p.kind && x.id === p.id))));
  const addFromList = () => {
    const have = new Set(typed.ok.map((x) => x.address.toLowerCase()));
    const add = (extras.data?.extras ?? []).filter((x) => listTicked.includes(x.id) && !x.stopped && !have.has(x.address.toLowerCase())).map((x) => x.address);
    if (add.length) setAlso((s) => [s.trim().replace(/[,;]+$/, ''), ...add].filter(Boolean).join('; '));
    setListTicked([]);
    setListOpen(false);
    say(t('ann176.sheet.fromListAdded', { count: add.length }));
    document.getElementById('ann-also')?.focus();
  };

  const save = async () => {
    setBusy('save');
    try {
      const d = await saveAnnouncement(input);
      setId(d.id);
      refresh(d.id);
      toast(t('ann176.sheet.saved'));
      say(t('ann176.sheet.saved'));
    } catch (e) {
      say(errorText(e), true);
    } finally {
      setBusy(null);
    }
  };
  const doPreview = async () => {
    setBusy('preview');
    setSure(false);
    try {
      const data = await previewAnnouncement(input);
      setPreview({ key: inputKey, data });
      if (!data.es) setPreviewLang('en');
      setProblem(null);
      setNote(null);
      announce(data.blocked ?? t('ann176.sheet.previewTitle'), data.blocked ? 'assertive' : 'polite');
      requestAnimationFrame(() => document.getElementById('h-ann-preview')?.focus());
    } catch (e) {
      say(errorText(e), true);
    } finally {
      setBusy(null);
    }
  };
  const send = async () => {
    setBusy('send');
    try {
      const r = await sendAnnouncement(input);
      toast(r.message);
      announce(r.message);
      refresh(r.announcement.id);
      onSent(r.announcement);
      onClose();
    } catch (e) {
      // 409 blocked and the rest: the server's own words
      say(errorText(e), true);
      setSure(false);
    } finally {
      setBusy(null);
    }
  };

  const upcoming = (events.data?.upcoming ?? []).filter((e) => e.status !== 'Cancelled');
  const hits = people.data?.people ?? [];
  const isTicked = (p: { kind: string; id: string }) => picked.some((x) => x.kind === p.kind && x.id === p.id);
  const names = c ? c.noEmail.map(person) : [];
  const sendOff = !!busy || bad.length > 0 || !!blocked;
  const shownText = shown ? (previewLang === 'es' && shown.es ? shown.es : shown.en) : null;
  const langName = (l: AnnounceLang) => t(l === 'es' ? 'ann176.sheet.spanish' : 'ann176.sheet.english');

  return (
    <SheetContent
      wide
      className="ann176-sheet"
      title={existing ? t('ann176.sheet.draftTitle') : t('ann176.sheet.newTitle')}
      returnFocusTo={returnFocusTo}
      footer={
        sure ? (
          <>
            <Button variant="secondary" disabled={!!busy} onClick={() => setSure(false)} data-testid="ann-send-not-now">
              {t('ann176.sheet.notNow')}
            </Button>
            <Button disabled={sendOff} onClick={() => send()} data-testid="ann-send-sure">
              {busy === 'send' ? t('ann176.sheet.working') : fresh && c ? t('ann176.sheet.sendSure', { count: c.total }) : t('ann176.sheet.sendSureUnknown')}
            </Button>
          </>
        ) : (
          <>
            <Button variant="secondary" disabled={!!busy} onClick={() => save()} data-testid="ann-save">
              {busy === 'save' ? t('ann176.sheet.working') : t('ann176.sheet.save')}
            </Button>
            <Button
              disabled={sendOff}
              aria-describedby={bad.length ? 'ann-bad' : blocked ? 'ann-blocked' : undefined}
              onClick={() => {
                if (!missing()) {
                  setProblem(null);
                  setSure(true);
                }
              }}
              data-testid="ann-send"
            >
              <Icon name="mail" /> {t('ann176.sheet.send')}
            </Button>
          </>
        )
      }
    >
      <div className="ann176-form" data-testid="ann-sheet">
        <div className="field">
          <label htmlFor="ann-subject-en">
            {t('ann176.sheet.subjectEn')}
            <span className="req">
              {' '}
              <span aria-hidden="true">*</span>
              <span className="sr-only">{t('ann176.sheet.required')}</span>
            </span>
          </label>
          <input id="ann-subject-en" className="input" lang="en" value={subjectEn} onChange={(e) => setSubjectEn(e.target.value)} maxLength={ANNOUNCE_MAX_SUBJECT} aria-required="true" autoComplete="off" data-testid="ann-subject-en" />
        </div>

        <fieldset className="field ann176-group">
          <legend className="eyebrow">{t('ann176.sheet.fieldsTitle')}</legend>
          <p className="help" id="ann-fields-help">
            {t('ann176.sheet.fieldsHelp')}
          </p>
          <div className="field">
            <label htmlFor="ann-event">{t('ann176.sheet.event')}</label>
            <select id="ann-event" className="input" value={eventId ?? ''} onChange={(e) => pickEvent(e.target.value)} aria-describedby="ann-event-help" data-testid="ann-event">
              <option value="">{t('ann176.sheet.eventNone')}</option>
              {/* a draft's event that is no longer upcoming stays chosen */}
              {eventId && !upcoming.some((e) => e.eventId === eventId) ? <option value={eventId}>{existing?.eventName ?? eventId}</option> : null}
              {upcoming.map((e) => (
                <option key={e.eventId} value={e.eventId}>
                  {e.name}
                </option>
              ))}
            </select>
            <p className="help" id="ann-event-help">
              {t('ann176.sheet.eventHelp')}
            </p>
          </div>
          <div className="field">
            <label htmlFor="ann-title">{t('ann176.sheet.title')}</label>
            <input id="ann-title" className="input" value={title} onChange={(e) => setTitle(e.target.value)} maxLength={200} autoComplete="off" aria-describedby="ann-fields-help" data-testid="ann-title" />
          </div>
          <div className="grid-2">
            <div className="field">
              <label htmlFor="ann-day">{t('ann176.sheet.day')}</label>
              <input id="ann-day" className="input" type="date" value={day} onChange={(e) => setDay(e.target.value)} data-testid="ann-day" />
            </div>
            <div className="field">
              <label htmlFor="ann-time">{t('ann176.sheet.time')}</label>
              <input id="ann-time" className="input" type="time" value={time} onChange={(e) => setTime(e.target.value)} data-testid="ann-time" />
            </div>
          </div>
          <div className="field">
            <label htmlFor="ann-place">{t('ann176.sheet.place')}</label>
            <input id="ann-place" className="input" value={place} onChange={(e) => setPlace(e.target.value)} maxLength={200} autoComplete="off" data-testid="ann-place" />
          </div>
          <div className="grid-2">
            <div className="field">
              <label htmlFor="ann-bring">{t('ann176.sheet.bring')}</label>
              <input id="ann-bring" className="input" value={bring} onChange={(e) => setBring(e.target.value)} maxLength={300} autoComplete="off" data-testid="ann-bring" />
            </div>
            <div className="field">
              <label htmlFor="ann-accessibility">{t('ann176.sheet.accessibility')}</label>
              <input id="ann-accessibility" className="input" value={accessibility} onChange={(e) => setAccessibility(e.target.value)} maxLength={300} autoComplete="off" data-testid="ann-accessibility" />
            </div>
          </div>
          <label className="switch">
            <input type="checkbox" checked={raffle} onChange={(e) => setRaffle(e.target.checked)} data-testid="ann-raffle" />
            <span>{t('ann176.sheet.raffle')}</span>
          </label>
          {raffle ? (
            <div className="field">
              <label htmlFor="ann-raffle-rules">{t('ann176.sheet.raffleRules')}</label>
              <input id="ann-raffle-rules" className="input" value={raffleRules} onChange={(e) => setRaffleRules(e.target.value)} maxLength={400} autoComplete="off" data-testid="ann-raffle-rules" />
            </div>
          ) : null}
        </fieldset>

        <section aria-labelledby="h-ann-message" className="ann176-group">
          <h3 id="h-ann-message" className="eyebrow">
            {t('ann176.sheet.message')}
          </h3>
          {!title.trim() && !body.en.trim() ? <p className="help">{t('ann176.sheet.needTitle')}</p> : null}
          <DraftBox
            value={body}
            onChange={setBody}
            request={request}
            autoDraft
            onDrafted={onDrafted}
            disabled={busy === 'send'}
            spanish={c ? { count: c.spanishMarked, total: c.total } : null}
            spanishExtra={
              <div className="field">
                <label htmlFor="ann-subject-es">{t('ann176.sheet.subjectEs')}</label>
                <input id="ann-subject-es" className="input" lang="es" value={subjectEs} onChange={(e) => setSubjectEs(e.target.value)} maxLength={ANNOUNCE_MAX_SUBJECT} autoComplete="off" data-testid="ann-subject-es" />
              </div>
            }
          />
        </section>

        <fieldset className="field ann176-group">
          <legend className="eyebrow">{t('ann176.sheet.audience')}</legend>
          <div className="radio-cards">
            {AUDIENCE_KINDS.map((k) => (
              <label key={k}>
                <input type="radio" name="ann-audience" checked={kind === k} onChange={() => setKind(k)} data-testid={`ann-audience-${k}`} />
                <span>{t(`ann176.sheet.kind.${k}`)}</span>
              </label>
            ))}
          </div>
          {kind === 'program' ? (
            <div className="field">
              <label htmlFor="ann-program">{t('ann176.sheet.program')}</label>
              <select id="ann-program" className="input" value={program} onChange={(e) => setProgram(e.target.value as AnnounceProgram)} data-testid="ann-program">
                {ANNOUNCE_PROGRAMS.map((p) => (
                  <option key={p} value={p}>
                    {t(`ann176.sheet.programs.${p}`)}
                  </option>
                ))}
              </select>
            </div>
          ) : null}
          {kind === 'people' ? (
            <div className="ann176-people">
              <div className="field">
                <label htmlFor="ann-people-search">{t('ann176.sheet.peopleSearch')}</label>
                <input id="ann-people-search" className="input" type="search" value={q} onChange={(e) => setQ(e.target.value)} maxLength={60} autoComplete="off" aria-describedby="ann-people-help" data-testid="ann-people-search" />
                <p className="help" id="ann-people-help">
                  {t('ann176.sheet.peopleHelp')}
                </p>
              </div>
              <div role="status">{q.trim().length >= 2 && people.isSuccess && !hits.length ? <p className="help">{t('ann176.sheet.peopleNone')}</p> : null}</div>
              {hits.length ? (
                <ul className="plain-list ann176-ticks" aria-label={t('ann176.sheet.matches')} data-testid="ann-people-hits">
                  {hits.map((p) => (
                    <li key={`${p.kind}-${p.id}`}>
                      <label>
                        <input type="checkbox" checked={isTicked(p)} onChange={(e) => tick(p, e.target.checked)} data-testid={`ann-person-${p.kind}-${p.id}`} />
                        <span>
                          {person(p)} · {t(`ann176.sheet.${p.kind}`)}
                          {!p.hasEmail ? <small> · {t('ann176.sheet.noEmailTag')}</small> : null}
                        </span>
                      </label>
                    </li>
                  ))}
                </ul>
              ) : null}
              <p className="help" data-testid="ann-people-count">
                {picked.length ? t('ann176.sheet.ticked', { count: picked.length }) : t('ann176.sheet.tickedNone')}
              </p>
              {picked.length ? (
                <ul className="plain-list ann176-ticks" aria-label={t('ann176.sheet.ticked', { count: picked.length })} data-testid="ann-people-picked">
                  {picked.map((p) => (
                    <li key={`${p.kind}-${p.id}`}>
                      <label>
                        <input type="checkbox" checked onChange={() => tick(p, false)} data-testid={`ann-picked-${p.kind}-${p.id}`} />
                        <span>
                          {person(p)} · {t(`ann176.sheet.${p.kind}`)}
                          {!p.hasEmail ? <small> · {t('ann176.sheet.noEmailTag')}</small> : null}
                        </span>
                      </label>
                    </li>
                  ))}
                </ul>
              ) : null}
            </div>
          ) : null}
        </fieldset>

        <div className="field">
          <label htmlFor="ann-also">{t('ann176.sheet.also')}</label>
          <textarea id="ann-also" className="input" rows={3} value={also} onChange={(e) => setAlso(e.target.value)} maxLength={20000} autoComplete="off" spellCheck={false} aria-describedby={`ann-also-help${bad.length ? ' ann-bad' : ''}`} aria-invalid={bad.length ? 'true' : undefined} data-testid="ann-also" />
          <p className="help" id="ann-also-help">
            {t('ann176.sheet.alsoHelp')}
          </p>
          {bad.length ? (
            <p className="field-err" role="alert" id="ann-bad" data-testid="ann-bad">
              <Icon name="alert" width={16} height={16} aria-hidden="true" /> {t('ann176.sheet.bad', { count: bad.length, list: bad.map((b) => `“${b}”`).join(', ') })}
            </p>
          ) : null}
          <div>
            <Button variant="secondary" size="sm" aria-expanded={listOpen} aria-controls="ann-from-list-panel" onClick={() => setListOpen((o) => !o)} data-testid="ann-from-list">
              <Icon name="plus" /> {t('ann176.sheet.fromList')}
            </Button>
          </div>
          {listOpen ? (
            <fieldset className="field ann176-fromlist" id="ann-from-list-panel" data-testid="ann-from-list-panel">
              <legend className="eyebrow">{t('ann176.sheet.fromListTitle')}</legend>
              {extras.isLoading ? <p role="status">{t('app.loading')}</p> : null}
              {extras.isError ? (
                <p className="field-err" role="alert">
                  {errorText(extras.error)}
                </p>
              ) : null}
              {extras.data && !extras.data.extras.length ? <p className="help">{t('ann176.sheet.fromListNone')}</p> : null}
              {extras.data?.extras.length ? (
                <>
                  <ul className="plain-list ann176-ticks">
                    {extras.data.extras.map((x) => (
                      <li key={x.id}>
                        <label>
                          <input type="checkbox" disabled={x.stopped} checked={listTicked.includes(x.id)} onChange={(e) => setListTicked((l) => (e.target.checked ? [...l, x.id] : l.filter((y) => y !== x.id)))} data-testid={`ann-from-list-${x.id}`} />
                          <span>
                            {x.name ? `${x.name} · ` : ''}
                            {x.address}
                            {x.stopped ? <small> · {t('ann176.sheet.fromListStopped')}</small> : null}
                          </span>
                        </label>
                      </li>
                    ))}
                  </ul>
                  <div>
                    <Button variant="secondary" size="sm" disabled={!listTicked.length} onClick={addFromList} data-testid="ann-from-list-add">
                      {t('ann176.sheet.fromListAdd')}
                    </Button>
                  </div>
                </>
              ) : null}
            </fieldset>
          ) : null}
        </div>

        <section aria-labelledby="h-ann-count" className="ann176-group ann176-countbox">
          <h3 id="h-ann-count" className="eyebrow">
            {t('ann176.sheet.countTitle')}
          </h3>
          <div role="status" className="ann176-stack">
            {!c && counting ? <p className="help">{t('ann176.sheet.counting')}</p> : null}
            {countError ? <p className="field-err">{countError}</p> : null}
            {c ? (
              <>
                <p className="ann176-count" data-testid="ann-count" aria-busy={counting}>
                  <b className="tnum">{groupsLine(t, c)}</b>
                  {c.noEmail.length ? (
                    <>
                      {' — '}
                      <span data-testid="ann-noemail">
                        {t('ann176.sheet.noEmail', { count: c.noEmail.length, names: names.slice(0, NAMES_SHOWN).join(', ') })}
                        {names.length > NAMES_SHOWN ? ` ${t('ann176.sheet.noEmailMore', { count: names.length - NAMES_SHOWN })}` : ''}
                      </span>
                    </>
                  ) : null}
                </p>
                {names.length > NAMES_SHOWN ? (
                  <details data-testid="ann-noemail-all">
                    <summary>{t('ann176.sheet.noEmailAll', { count: names.length })}</summary>
                    <p className="help">{names.join(', ')}</p>
                  </details>
                ) : null}
                {c.tenantsStopped ? <p className="help">{t('ann176.sheet.tenantsStopped', { count: c.tenantsStopped })}</p> : null}
                {c.volunteersNoEmail ? <p className="help">{t('ann176.sheet.volunteersNoEmail', { count: c.volunteersNoEmail })}</p> : null}
                {c.volunteersStopped ? <p className="help">{t('ann176.sheet.volunteersStopped', { count: c.volunteersStopped })}</p> : null}
                {c.extraStopped.length ? <p className="help">{t('ann176.sheet.extraStopped', { list: c.extraStopped.join(', ') })}</p> : null}
                {c.duplicates ? <p className="help">{t('ann176.sheet.duplicates', { count: c.duplicates })}</p> : null}
              </>
            ) : null}
          </div>
          <p className="help ann176-hiding">
            <Icon name="lock" aria-hidden="true" /> <span data-testid="ann-hiding">{t('ann176.sheet.hiding')}</span>
          </p>
          <div>
            <Button variant="secondary" disabled={!!busy} onClick={() => doPreview()} data-testid="ann-preview">
              {busy === 'preview' ? t('ann176.sheet.previewing') : t('ann176.sheet.preview')}
            </Button>
          </div>
        </section>

        {preview ? (
          <section aria-labelledby="h-ann-preview" className="ann176-group" data-testid="ann-preview-panel">
            <h3 id="h-ann-preview" tabIndex={-1}>
              {t('ann176.sheet.previewTitle')}
            </h3>
            {!shown ? (
              <p className="warn" data-testid="ann-preview-old">
                <Icon name="info" aria-hidden="true" />
                <span>{t('ann176.sheet.previewOld')}</span>
              </p>
            ) : (
              <>
                {blocked ? (
                  <p className="field-err" role="alert" id="ann-blocked" data-testid="ann-blocked">
                    <Icon name="alert" width={16} height={16} aria-hidden="true" /> {t('ann176.sheet.sendOff', { why: blocked })}
                  </p>
                ) : null}
                {shown.es ? (
                  <div className="seg" role="group" aria-label={t('ann176.sheet.previewLang')}>
                    {(['en', 'es'] as const).map((l) => (
                      <button key={l} type="button" aria-pressed={previewLang === l} onClick={() => setPreviewLang(l)} data-testid={`ann-preview-lang-${l}`}>
                        {langName(l)}
                      </button>
                    ))}
                  </div>
                ) : (
                  <p className="help" data-testid="ann-preview-no-spanish">
                    {t('ann176.sheet.noSpanish')}
                  </p>
                )}
                {shown.audience.spanishMarked > 0 ? (
                  <p className="help" data-testid="ann-preview-spanish-line">
                    {t(shown.audience.spanish > 0 ? 'oneLang.line' : 'oneLang.lineNoText', { count: shown.audience.spanishMarked, total: shown.audience.total })}
                  </p>
                ) : null}
                <p className="help" data-testid="ann-preview-subject">
                  {t('ann176.sheet.previewSubject', { subject: shownText!.subject })}
                </p>
                <div className="ann176-frame">
                  <iframe title={t('ann176.sheet.previewFrame', { lang: langName(shown.es ? previewLang : 'en') })} srcDoc={shownText!.html} sandbox="" data-testid="ann-preview-frame" />
                </div>
                <p data-testid="ann-preview-count">{t('ann176.sheet.previewCount', { people: t('ann176.n.people', { count: shown.audience.total }), messages: t('ann176.n.messages', { count: shown.audience.batches }), when: nyWhen(shown.audience.firstBatchAt, lang) })}</p>
              </>
            )}
          </section>
        ) : null}

        {problem ? (
          <p className="field-err" role="alert" data-testid="ann-msg">
            <Icon name="alert" width={16} height={16} aria-hidden="true" /> {problem}
          </p>
        ) : null}
        {note ? (
          <p className="help" data-testid="ann-note">
            {note}
          </p>
        ) : null}
      </div>
    </SheetContent>
  );
}
